'use client'
import { Popover, Transition } from '@headlessui/react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import React, { Fragment, useEffect, useState } from 'react'
import useScrollToTop from 'hoc/scroll-to-top'
import Button from './button'
import { useAppDispatch, useAppSelector } from 'features/hooks'
import { RootState } from 'features/store'
import { useUserProfileQuery } from 'graphql/generated/graphql'
import {
  Menu as NavMenu,
  MenuHandler,
  MenuList,
  MenuItem as NavMenuItem,
  Avatar,
  Typography,
  Dialog,
  Card,
  CardBody,
  IconButton,
  Select,
  Option,
  Button as MTButton,
} from '@material-tailwind/react'
import {
  BellSimple,
  CaretDown,
  LockOpen,
  Swap,
  UsersThree,
} from '@phosphor-icons/react'
import { AiOutlineUser } from 'react-icons/ai'
import { MdLogout } from 'react-icons/md'
import { logOut, logOutAndClearReminder } from 'features/authSlice'
import { appHomePath } from '@/lib/appHomePath'
import { LOGGED_OUT_HOME_PATH } from '@/lib/landingTest'
import { isPublicNavActive, publicNavItems, PublicNavDropdown } from '@/components/publicNav'

export const Navbar = () => {
  const pathname = usePathname()
  const dispatch = useAppDispatch()
  const router = useRouter()

  const { jwt_token, user } = useAppSelector(
    (state: RootState) => state.culineerAuth
  )
  const { data: userDetail } = useUserProfileQuery({
    variables: {
      slug: user?.slug,
    },
    skip: !user?.slug,
  })

  // useEffect(() => {
  //   if (userDetail) {
  //     router.push('/discover')
  //   }
  // }, [userDetail, router])

  useScrollToTop()

  const homeHref = user?.user_id ? appHomePath(user) : LOGGED_OUT_HOME_PATH

  const routes = publicNavItems(homeHref)

  const isActive = (path: string) => isPublicNavActive(pathname, path)

  const DesktopMenu = () => (
    <nav className="hidden items-center gap-4 font-[400px] tracking-wide text-[#202020] lg:flex xl:gap-6 2xl:gap-8">
      {routes.map((item) =>
        item.items ? (
          <PublicNavDropdown
            key={item.label}
            label={item.label}
            items={item.items}
            pathname={pathname}
          />
        ) : (
          <Link
            key={item.href}
            href={item.href || '/'}
            className={isActive(item.href || '/') ? 'text-[#85C349]' : 'hover:text-[#85C349]'}
          >
            {item.label}
          </Link>
        )
      )}
    </nav>
  )

  const logout = () => {
    dispatch(logOutAndClearReminder())
    router.push('/')
  }

  const getProfileSrc = () => {
    let profileImgUrl
    if(userDetail?.userProfile?.user_type?.toLowerCase() === 'general') {
      profileImgUrl = userDetail?.userProfile?.profile_photo_resource || '/assets/avatar/user-default.svg'
    } else if (userDetail?.userProfile?.user_type?.toLowerCase() === 'producer') {
      profileImgUrl = userDetail?.userProfile?.default_listing?.photo || '/assets/icons/tractor.svg'
    } else if (userDetail?.userProfile?.user_type?.toLowerCase() === 'market') {
      profileImgUrl = userDetail?.userProfile?.default_listing?.photo || '/assets/icons/Market.svg'
    }

    return profileImgUrl
  }

  const MobileMenu = () => (
    <Popover>
      <Popover.Button
        className="focus:outline-none inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#85C349] font-medium text-white transition-all hover:bg-[#85C349]/90 sm:h-11 sm:w-11"
        aria-label="menu"
      >
        <Image
          src="/images/mobileMenu.svg"
          alt={'menu'}
          width={20}
          height={20}
          className='pointer-events-none'
          loading="lazy"
        />
      </Popover.Button>
      <Popover.Overlay className="fixed inset-0 z-20 bg-black opacity-30" />
      <Transition
        as={Fragment}
        enter="transition duration-100 ease-out"
        enterFrom="transform -translate-x-full"
        enterTo="transform translate-x-0"
        leave="transition duration-75 ease-out"
        leaveFrom="transform translate-x-0"
        leaveTo="transform -translate-x-full"
      >
        <Popover.Panel className="fixed left-0 top-0 z-20 h-screen w-full bg-white p-6 md:w-96">
          {({ close }) => (
            <div>
              <div className="flex justify-between">
                <Link href={homeHref} onClick={() => close()}>
                  <Image
                    src="/images/logo.svg"
                    alt="Culinear"
                    className="w-36 pointer-events-none"
                    width={20}
                    height={20}
                  />
                </Link>
                <div className="z-50 flex items-center gap-2 lg:hidden">
                  <Button
                    onClick={close}
                    className="!p-0"
                    title={
                      <Image
                        src="/images/close.svg"
                        alt={'menu'}
                        width={40}
                        height={40}
                        className='pointer-events-none'
                      />
                    }
                    bgGreen={false}
                  />
                </div>
              </div>

              <ul className="m-8 flex flex-col items-center">
                {routes.map((item) =>
                  item.items ? (
                    <li key={item.label} className="mb-4 w-full text-center">
                      <p className="mb-2 font-semibold text-[#202020]">{item.label}</p>
                      <ul>
                        {item.items.map((child) => (
                          <li key={child.href} className="mb-2">
                            <Link
                              href={child.href}
                              onClick={() => close()}
                              className={
                                isActive(child.href)
                                  ? 'text-[#85C349]'
                                  : 'text-[#555554] hover:text-[#85C349]'
                              }
                            >
                              {child.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ) : (
                    <li
                      key={item.label}
                      className={`mb-4 text-center text-[#555554] md:text-start ${
                        isActive(item.href || '/') ? 'text-[#85C349]' : 'hover:text-[#85C349]'
                      }`}
                    >
                      <Link href={item.href || '/'} onClick={() => close()}>
                        {item.label}
                      </Link>
                    </li>
                  )
                )}
                <li>
                  <Button
                    title={'Sign in'}
                    href="/auth/login"
                    bgGreen={false}
                    className="mb-4"
                    onClick={() => close()}
                  />
                </li>
                <li>
                  <Button
                    title={'Join Culineer'}
                    href="/auth/join-culineer"
                    className="px-10"
                    onClick={() => close()}
                  />
                </li>
              </ul>
            </div>
          )}
        </Popover.Panel>
      </Transition>
    </Popover>
  )

  return (
    <header className="bg-white shadow-md">
      <div className="mx-auto flex max-w-[1536px] items-center justify-between px-4 py-4 md:px-8 lg:px-16">
        <Link href={homeHref} className="flex items-center space-x-2">
          <Image
            src="/images/logo.svg"
            alt="Culinear"
            className="pointer-events-none w-28 sm:w-32 lg:w-36"
            width={20}
            height={20}
            loading="lazy"
          />
        </Link>
        <DesktopMenu />
        <div className="hidden gap-3 lg:flex ">
          {userDetail ? (
            <NavMenu>
              <MenuHandler>
                <div className="flex items-center justify-center space-x-2">
                  <Avatar
                    variant="circular"
                    alt={userDetail?.userProfile?.full_name?.replace(' ', '-')}
                    className="h-8 w-8 cursor-pointer"
                    src={getProfileSrc()}
                  />
                  <div className="flex items-center space-x-2">
                    <h2 className="sm:block xs:hidden">
                      {userDetail?.userProfile?.first_name ||
                        userDetail?.userProfile?.full_name}
                    </h2>
                    <CaretDown size={20} className="h-3.5 w-3.5" />
                  </div>
                </div>
              </MenuHandler>
              <MenuList className="mx-0">
                <NavMenuItem className="flex items-center gap-2">
                  <AiOutlineUser size={20} />
                  <Typography
                    variant="small"
                    className="font-medium"
                    as={Link}
                    href={`${userDetail?.userProfile?.user_type?.toLowerCase() === 'general' ? '/member/'+userDetail?.userProfile?.slug : '/'+userDetail?.userProfile?.user_type?.toLowerCase()+'/'+userDetail?.userProfile?.default_listing?.slug}`}
                  >
                    My Profile
                  </Typography>
                </NavMenuItem>
                {/* <NavMenuItem className="flex items-center gap-2">
                    <LiaShoppingCartSolid size={20} />
                    <Typography variant="small" className="font-medium" as={Link} href={'/profile'}>
                        Shopping
                    </Typography>
                </NavMenuItem>
                <NavMenuItem className="flex items-center gap-2">
                    <HiOutlineUserGroup size={20} />
                    <Typography variant="small" className="font-medium" as={Link} href={'/profile'}>
                        Community
                    </Typography>
                </NavMenuItem> */}

                <NavMenuItem
                  className="flex items-center gap-2"
                  onClick={logout}
                >
                  <MdLogout size={20} />
                  <Typography variant="small" className="font-medium">
                    Log Out
                  </Typography>
                </NavMenuItem>
              </MenuList>
            </NavMenu>
          ) : (
            <>
              <Button title={'Sign in'} href="/auth/login" bgGreen={false} />
              <Button
                title={'Join Culineer'}
                href="/auth/join-culineer"
                className="whitespace-nowrap"
              />
            </>
          )}
        </div>

        <div className="z-50 flex items-center gap-2 lg:hidden">
          <Button
            title={'Join Culineer'}
            href="/auth/join-culineer"
            className="whitespace-nowrap"
          />
          <div className="flex-shrink-0">
            <MobileMenu />
          </div>
        </div>
      </div>
    </header>
  )
}
